// Data model for the Rights Dashboard (דשבורד זכויות).
//
// Core principle from the spec: every datum is a *visible field object*, not a
// bare value. A field carries its value plus the metadata that makes the whole
// system explainable — where it came from, whether a human approved it, and how
// certain we are. The matrices and report are *views* over these objects, never
// the source of truth.

import { makeMatrix } from './matrix';

export const SCHEMA_VERSION = 3;

// ---- Data status (סטטוס נתון) -------------------------------------------------
export const STATUS = {
  APPROVED: 'approved',        // מאושר — checked & approved by the user
  AUTO: 'auto_extracted',      // חולץ אוטומטית — extracted by system/AI, awaiting approval
  MANUAL: 'manual',            // הוזן ידנית — entered by the user
  ESTIMATED: 'estimated',      // משוער — derived from a partial source/estimate
  MISSING: 'missing',          // חסר — not found / not entered
  CONFLICT: 'conflict',        // סותר — sources disagree
};

export const STATUS_LABELS = {
  [STATUS.APPROVED]: 'מאושר',
  [STATUS.AUTO]: 'חולץ אוטומטית',
  [STATUS.MANUAL]: 'הוזן ידנית',
  [STATUS.ESTIMATED]: 'משוער',
  [STATUS.MISSING]: 'חסר',
  [STATUS.CONFLICT]: 'סותר',
};

// ---- Project types (סוג פרויקט) ----------------------------------------------
export const PROJECT_TYPES = [
  { id: 'empty_lot', label: 'מגרש ריק / לא בנוי' },
  { id: 'existing_building', label: 'מבנה קיים' },
  { id: 'renewal_addition', label: 'התחדשות / תוספת' },
  { id: 'demolition_rebuild', label: 'הריסה ובנייה' },
  { id: 'rights_check', label: 'בדיקת זכויות ראשונית' },
];

// ---- Plan Stack roles (תפקיד בחישוב) -----------------------------------------
export const PLAN_ROLE = {
  MAIN_RIGHTS: 'main_rights',
  MODIFIER: 'modifier',
  POTENTIAL_CEILING: 'potential_ceiling',
  INFLUENCE: 'influence_layer',
  GUIDELINES: 'authority_guidelines',
  EXISTING_PERMIT: 'existing_permit',
  UNCLASSIFIED: 'unclassified',
};

export const PLAN_ROLE_OPTIONS = [
  { id: PLAN_ROLE.MAIN_RIGHTS, label: 'מקור זכויות ראשי' },
  { id: PLAN_ROLE.MODIFIER, label: 'תכנית משלימה — שינוי נקודתי' },
  { id: PLAN_ROLE.POTENTIAL_CEILING, label: 'פוטנציאל / תקרה' },
  { id: PLAN_ROLE.INFLUENCE, label: 'שכבת השפעה / מגבלה' },
  { id: PLAN_ROLE.GUIDELINES, label: 'הנחיות מרחביות' },
  { id: PLAN_ROLE.EXISTING_PERMIT, label: 'היתר קיים' },
  { id: PLAN_ROLE.UNCLASSIFIED, label: 'לא סווג' },
];

export function planRoleLabel(id) {
  return (PLAN_ROLE_OPTIONS.find(o => o.id === id) || {}).label || 'לא סווג';
}

// ---- id helper ---------------------------------------------------------------
export function uid(prefix = 'id') {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
}

// Site element (מצב קיים — מגרש): עצים, חניות, גדרות, זיקות, הפקעות, קווי בניין...
export const SITE_ELEMENT_TYPES = [
  { id: 'trees', label: 'עצים / צמחייה' },
  { id: 'parking', label: 'חניות / חניית עפר' },
  { id: 'fences', label: 'גדרות / שערים / קירות' },
  { id: 'easements', label: 'זיקות הנאה' },
  { id: 'expropriation', label: 'הפקעות' },
  { id: 'building_lines', label: 'קווי בניין' },
  { id: 'access', label: 'גישות רכב / הולכי רגל' },
  { id: 'topography', label: 'טופוגרפיה / מפלסים' },
  { id: 'infrastructure', label: 'תשתיות' },
  { id: 'use', label: 'שימוש קיים בפועל' },
  { id: 'other', label: 'אחר' },
];

export function siteElementTypeLabel(id) {
  return (SITE_ELEMENT_TYPES.find((o) => o.id === id) || {}).label || id;
}

export function makeSiteElement(init = {}) {
  return {
    id: uid('site'),
    type: init.type || 'other',
    description: init.description || '',
    status: init.status || STATUS.MANUAL,
    note: init.note || null,
  };
}

// ---- field factory -----------------------------------------------------------
// A field is { value, status, source, confidence, note }. When no explicit
// status is given we infer MISSING for empty values and MANUAL otherwise, so a
// blank project reads as "everything is a task to fill", per the spec.
export function field(value = null, opts = {}) {
  const empty = value === null || value === undefined || value === '';
  return {
    value: empty ? null : value,
    status: opts.status || (empty ? STATUS.MISSING : STATUS.MANUAL),
    source: opts.source || null,
    sourceUrl: opts.sourceUrl || null, // clickable evidence link (PDF page / viewer)
    confidence: opts.confidence ?? null,
    note: opts.note || null,
  };
}

export function isFilled(f) {
  return f && f.value !== null && f.value !== undefined && f.value !== '';
}

// ---- Plan Stack item ---------------------------------------------------------
export function makePlanStackItem(init = {}) {
  const auto = init.origin === 'auto';
  const st = auto ? STATUS.AUTO : STATUS.MANUAL;
  return {
    id: uid('plan'),
    planRef: init.planRef || null,
    approvalDate: init.approvalDate || null,
    planStatus: init.planStatus || null,
    planUrl: init.planUrl || null,
    zones: init.zones || [],          // raw extracted zoning rules (read-only evidence)
    origin: init.origin || 'manual',
    // Manual m² this layer adds to the rights envelope (multi-layer calc).
    rightsAddition: field(),
    title: field(init.title ?? null, { status: init.title ? st : STATUS.MISSING, source: init.source }),
    type: field(init.type ?? null, { status: init.type ? st : STATUS.MISSING, source: init.source }),
    role: field(init.role ?? PLAN_ROLE.UNCLASSIFIED, { status: init.role ? st : STATUS.MISSING, source: init.source }),
    influence: field(init.influence ?? null, { status: init.influence ? st : STATUS.MISSING, source: init.source }),
  };
}

// ---- Permit timeline item ----------------------------------------------------
export function makePermitItem(init = {}) {
  return {
    id: uid('permit'),
    year: init.year || null,
    document: init.document || null,
    action: init.action || null,
    statusValue: init.statusValue || STATUS.AUTO, // permit records arrive auto-extracted
    requestNumber: init.requestNumber || null,
    url: init.url || null, // evidence link (scanned page) when known
    forms: init.forms || [],
  };
}

// ---- Document library item ---------------------------------------------------
// Kinds stored as the Hebrew label directly — the missing-data check greps for
// נסח/טאבו in name+kind, so labels double as machine-checkable tags.
export const DOC_KINDS = ['נסח טאבו', 'מדידה', 'היתר', 'תכנית', 'תמונות', 'סיכום פגישה', 'אחר'];

export function makeDocument(init = {}) {
  return {
    id: uid('doc'),
    name: init.name || '',
    kind: init.kind || null,   // נסח / מדידה / היתר / תמונות ...
    url: init.url || null,
    status: init.status || STATUS.MANUAL,
    note: init.note || null,
  };
}

// ---- Scenarios (מצב מוצע) ----------------------------------------------------
export const SCENARIO_GOALS = [
  { id: 'realize_vested', label: 'מימוש זכויות מוקנות' },
  { id: 'max_units', label: 'מקסימום יח"ד' },
  { id: 'tenant_benefits', label: 'תמורות לדיירים' },
  { id: 'balanced', label: 'תרחיש יזמי מאוזן' },
  { id: 'min_service', label: 'שירות מינימלי' },
  { id: 'no_ground_main', label: 'ללא עיקרי בקרקע' },
  { id: 'far_potential', label: 'פוטנציאל רח"ק' },
];

export function scenarioGoalLabel(id) {
  return (SCENARIO_GOALS.find((o) => o.id === id) || {}).label || '';
}

export function makeScenario(init = {}) {
  return {
    id: uid('scen'),
    name: init.name || 'תרחיש חדש',
    goal: init.goal || 'realize_vested',
    program: {
      freeText: '',           // developer program in natural language
      params: [],             // [{ key, label, value, source }] — AI-translated or manual
      targetUnits: null,      // numeric target used by the fit engine
    },
    matrix: makeMatrix(),     // proposed-state matrix (same shape as existing)
  };
}

// ---- blank project (the Live Calculation State seed) -------------------------
export function blankProject() {
  const now = Date.now();
  return {
    id: uid('proj'),
    schemaVersion: SCHEMA_VERSION,
    createdAt: now,
    updatedAt: now,

    project: {
      name: field(),
      projectType: field(),
      client: field(),        // מזמין העבודה
      developer: field(),     // יזם
      rightsHolders: field(), // בעלי זכויות ודיירים קיימים
      consultants: field(),   // אדריכל / מודד / שמאי / עו"ד / יועצים
      contacts: field(),
    },

    property: {
      address: field(),
      gush: field(),
      helka: field(),
      subParcels: field(),
      parcelArea: field(),    // שטח חלקה (מ"ר)
      localAuthority: field(),
      committee: field(),
      gisUrl: field(),
      gmapsUrl: field(),
      planningDbUrl: field(),
      landUse: field(),      // ייעוד החלקה (רצף מגרשים)
      landUseMeta: null,     // { designation, plan, parcel } — structured, drives rights seeding
      coords: { x_itm: null, y_itm: null },
      notes: field(),
    },

    documents: [],
    planStack: [],
    permitTimeline: [],
    permitsMeta: null, // { total, kept, filtered_out } — building-scoping stats from autofill

    existingState: {
      // Parcel-specific as-built summary (seeded from תיק בניין / permits).
      summary: {
        builtArea: field(),    // שטח בנוי (מ"ר)
        units: field(),        // יח"ד
        rooms: field(),        // חדרים
        volume: field(),       // נפח (מ"ק)
        storiesAbove: field(), // קומות מעל הכניסה הקובעת
        storiesBelow: field(), // קומות מתחת
        height: field(),       // גובה (מ')
        footprint: field(),    // תכסית קיימת — טביעת רגל קרקע (מ"ר)
      },
      matrix: makeMatrix(),
      apartments: [],
      siteElements: [],
      description: field(),
      builtAsRaw: null,
    },
    scenarios: [],
    activeScenarioId: null,
    // Rights inputs feeding the deterministic engine. Seeded from the main-rights
    // plan on autofill (tagged AUTO, awaiting approval); editable by the user.
    rights: {
      farPct: field(),            // אחוזי בנייה %
      coveragePct: field(),       // תכסית %
      maxStories: field(),        // קומות
      maxHeight: field(),         // גובה (מ')
      densityPerDunam: field(),   // יח"ד לדונם
      parkingRatio: field(),      // חניות ליח"ד
      calcMethod: field('total'), // 'total' | 'split'
      sourceRef: null,            // plan_ref the seed came from
    },
    calcResult: null,
    missingData: [],
    resolvedIssues: [], // ids of advisory items the user marked "תקין · טופל"
    auditLog: [],
  };
}

// Classify a plan by *type* from its ref/title/status. Transparent heuristic,
// not authoritative — the user reassigns, and a real AI classifier replaces it
// later. The key correctness point (spec §4.5): a comprehensive master plan
// (כוללנית/מתאר) signals potential/ceiling, NOT vested rights, and a national
// plan (תמ״א/מטרו) is an influence layer — neither should be auto-promoted to
// "main rights source". Only an approved *detailed* plan carrying FAR qualifies.
export function classifyPlan(plan) {
  const hay = `${plan.planRef || plan.plan_ref || ''} ${plan.title || ''}`;
  const status = String(plan.planStatus || plan.status || '').toLowerCase();
  const hasFar = (plan.zones || []).some((z) => z.max_far_pct != null);

  if (status === 'rejected' || status === 'cancelled') return 'rejected';
  if (/תמ[״"']?א|מטרו|תת[״"']?ל/.test(hay)) return 'national';      // תמ״א / מטרו / תת״ל
  if (/מתאר|כוללני/.test(hay)) return 'comprehensive';              // master / comprehensive
  if (hasFar) return 'detailed';
  return 'other';
}

const CATEGORY_ROLE = {
  national: PLAN_ROLE.INFLUENCE,
  comprehensive: PLAN_ROLE.POTENTIAL_CEILING,
  detailed: PLAN_ROLE.MODIFIER,   // upgraded to MAIN_RIGHTS for the single best detailed plan
  rejected: PLAN_ROLE.UNCLASSIFIED,
  other: PLAN_ROLE.UNCLASSIFIED,
};

export function categoryToRole(cat) {
  return CATEGORY_ROLE[cat] || PLAN_ROLE.UNCLASSIFIED;
}

export const CATEGORY_LABELS = {
  national: 'תכנית ארצית / השפעה',
  comprehensive: 'תכנית כוללנית / מתאר',
  detailed: 'תכנית מפורטת',
  rejected: 'תכנית שנדחתה',
  other: 'אחר',
};
